/** @format */
/** @format */

import React, { FC } from 'react'
import styled from 'styled-components'
import {
  FormItem,
  Input,
  Select,
  Option,
  Button,
  Icon,
  Switch,
  Spinner,
  Tooltip
} from '@king-design/react'
import { EditPolicyRuleProps } from 'src/pages/PrometheusMonitor/Alarm/constants'
import { cloneDeep } from 'lodash'
import { PromQLEditor } from 'src/components'

const labelsKey = {
  summary: '告警摘要',
  description: '告警内容'
}

interface RuleFormItemProps {
  className?: string
  index: number
  ruleValue: EditPolicyRuleProps
  onChangeValue: (value: Partial<EditPolicyRuleProps>, index) => void
  ruleNameUnique: (value: string) => boolean | string
  page?: string // 页面类型，policy/template
}
const RuleFormItem: FC<RuleFormItemProps> = ({
  className,
  index,
  ruleValue,
  onChangeValue,
  ruleNameUnique,
  page = 'policy'
}) => {
  const labelNameUnique = (value: string) => {
    let count = 0
    if (value && ruleValue?.Labels?.length) {
      ruleValue?.Labels.find(item => {
        if (item.Name === value) {
          count++
        }
        return count > 1
      })
      return count === 1 || '同一个规则内，label-name不允许重复'
    }
    return true
  }
  const annotateNameUnique = (value: string) => {
    let count = 0
    if (value && ruleValue?.Annotations?.length) {
      ruleValue?.Annotations.find(item => {
        if (item.Name === value) {
          count++
        }
        return count > 1
      })
      return count === 1 || '同一个规则内，annotation-name不允许重复'
    }
    return true
  }
  return (
    <div className={className}>
      {!ruleValue.Expand ? (
        <div
          className="rule_item_header"
          onClick={() => {
            onChangeValue({ Expand: true }, index)
          }}
        >
          <p>
            <i style={{ marginRight: 8 }}>#.{index + 1}</i>
            <span>{ruleValue?.Alert || ''}</span>
          </p>
          <div className="ksc-space">
            <Icon className="k-icon-down" size={16} />
            <Icon
              className="ion-trash-a"
              size={16}
              onClick={() => {
                onChangeValue(undefined, index)
              }}
            />
          </div>
        </div>
      ) : null}
      <FormItem
        className={`rule_item_container ${!ruleValue.Expand ? 'rule_item_container_hide' : ''}`}
        hidden={!ruleValue.Expand}
        hideLabel
      >
        <div className="header">
          <p className="title">
            <i style={{ marginRight: 8 }}>#.{index + 1}</i>
          </p>
          <div className="ksc-space">
            <Icon
              className="k-icon-up"
              size={16}
              onClick={() => {
                onChangeValue({ Expand: false }, index)
              }}
            />
            <Icon
              className="ion-trash-a"
              size={16}
              onClick={() => {
                onChangeValue(undefined, index)
              }}
            />
          </div>
        </div>
        {page !== 'template' ? (
          <FormItem label="规则启停" value={ruleValue.State}>
            <Switch
              size="small"
              value={ruleValue.State === 1}
              onChangeValue={value => {
                onChangeValue({ State: value ? 1 : 2 }, index)
              }}
            />
          </FormItem>
        ) : null}
        <FormItem
          label="规则名称(alert)"
          value={ruleValue.Alert}
          rules={{
            required: true,
            userName: true,
            unique: value => {
              return ruleNameUnique ? ruleNameUnique(value) : true
            }
          }}
        >
          <Input
            size="small"
            onChangeValue={value => {
              onChangeValue({ Alert: value }, index)
            }}
            placeholder="请输入规则名称"
            value={ruleValue.Alert}
          ></Input>
        </FormItem>
        <FormItem
          className="form_item_expr"
          label="规则表达式(expr)"
          value={ruleValue.Expr}
          rules={{ required: true }}
        >
          <PromQLEditor
            value={ruleValue.Expr}
            onChange={value => {
              onChangeValue({ Expr: value }, index)
            }}
          />
        </FormItem>
        <FormItem
          className="for_form_item"
          slotLabel={
            <>
              持续时间(for)
              <Tooltip
                content="触发规则的最小持续时间。
                如：若设置为0，则满足规则时立刻触发告警。若设置为1分钟，则持续满足规则1分钟后触发告警，1分钟内视为正常波动不触发告警。"
              >
                <Icon className="icon ion-help-circled" />
              </Tooltip>
            </>
          }
          value={ruleValue.For}
          rules={{ required: true, max: ruleValue.ForUnit === 'h' ? 168 : 10080 }}
        >
          <Spinner
            size="small"
            width={80}
            value={parseInt(ruleValue.For + '')}
            min={0}
            forceStep
            onChangeValue={value => {
              onChangeValue({ ...ruleValue, For: value }, index)
            }}
          />
          <Select
            size="mini"
            container={() => document.getElementsByClassName('for_form_item')[index]}
            style={{ width: '80px', height: '26px', marginLeft: 8 }}
            value={ruleValue.ForUnit}
            onChangeValue={value => {
              onChangeValue({ ...ruleValue, ForUnit: value }, index)
            }}
          >
            <Option value="m">分钟</Option>
            <Option value="h">小时</Option>
          </Select>
        </FormItem>
        <FormItem
          label="标签(labels)"
          className="form_item_list"
          value={ruleValue.Labels}
          rules={{ maxLength: 15 }}
        >
          {ruleValue.Labels.map((item, labelIndex) => {
            return (
              <div className="list_item" key={`labels-${labelIndex}`}>
                <FormItem
                  className="list_item_key"
                  hideLabel
                  value={item.Name}
                  rules={{
                    required: true,
                    unique: labelNameUnique,
                    ruleLabelKey: true
                  }}
                >
                  {labelIndex === 0 ? (
                    <p
                      style={{ width: 130, marginBottom: 0, textAlign: 'right' }}
                    >{`告警等级(${item.Name})`}</p>
                  ) : (
                    <Input
                      size="small"
                      width={130}
                      value={item.Name}
                      onChangeValue={value => {
                        const copyLabels = cloneDeep(ruleValue.Labels)
                        copyLabels[labelIndex].Name = value
                        onChangeValue({ Labels: copyLabels }, index)
                      }}
                    />
                  )}
                </FormItem>
                <span>=</span>
                <FormItem
                  hideLabel
                  value={item.Value}
                  rules={{
                    required: true,
                    ruleDescription: true
                  }}
                >
                  {labelIndex === 0 ? (
                    <>
                      <Select
                        size="mini"
                        container={() => document.getElementsByClassName('form_item_list')[index]}
                        style={{ width: '150px', height: '24px' }}
                        value={item.Value}
                        onChangeValue={value => {
                          const copyLabels = cloneDeep(ruleValue.Labels)
                          copyLabels[labelIndex].Value = value
                          onChangeValue({ Labels: copyLabels }, index)
                        }}
                      >
                        <Option value="critical">critical</Option>
                        <Option value="error">error</Option>
                        <Option value="warning">warning</Option>
                      </Select>
                      <Tooltip
                        slotContent={
                          <>
                            <p style={{ margin: 0 }}>critical: 需要立即处理的告警;</p>
                            <p style={{ margin: 0 }}>error: 产生错误，可能需要处理;</p>
                            <p style={{ margin: 0 }}>warning: 存在风险;</p>
                          </>
                        }
                      >
                        <Icon className="icon ion-help-circled" />
                      </Tooltip>
                    </>
                  ) : (
                    <Input
                      size="small"
                      width={322}
                      type="textarea"
                      value={item.Value}
                      onChangeValue={value => {
                        const copyLabels = cloneDeep(ruleValue.Labels)
                        copyLabels[labelIndex].Value = value
                        onChangeValue({ Labels: copyLabels }, index)
                      }}
                    />
                  )}
                </FormItem>
                {labelIndex > 0 ? (
                  <Icon
                    className="ion-trash-a"
                    size={16}
                    onClick={() => {
                      const copyLabels = cloneDeep(ruleValue.Labels)
                      copyLabels.splice(labelIndex, 1)
                      onChangeValue(
                        {
                          Labels: copyLabels
                        },
                        index
                      )
                    }}
                  />
                ) : null}
              </div>
            )
          })}
          <Button
            className="form_add_btn"
            size="small"
            onClick={() => {
              const copyLabels = cloneDeep(ruleValue.Labels)
              copyLabels.push({ Name: '', Value: '' })
              onChangeValue(
                {
                  Labels: copyLabels
                },
                index
              )
            }}
          >
            <Icon className="k-icon-add-bold" />
            添加
          </Button>
        </FormItem>
        <FormItem
          label="注解(annotations)"
          className="form_item_list"
          value={ruleValue.Annotations}
          rules={{ maxLength: 15 }}
        >
          {ruleValue.Annotations.map((item, itemIndex) => {
            return (
              <div className="list_item" key={`annotations-${itemIndex}`}>
                <FormItem
                  hideLabel
                  className="list_item_key"
                  value={item.Name}
                  rules={{
                    required: true,
                    unique: annotateNameUnique,
                    ruleLabelKey: true
                  }}
                >
                  {labelsKey[item.Name] ? (
                    <p style={{ width: 130, marginBottom: 0, textAlign: 'right' }}>
                      {labelsKey[item.Name]}({item.Name})
                    </p>
                  ) : (
                    <Input
                      size="small"
                      width={130}
                      value={item.Name}
                      onChangeValue={value => {
                        const copyAnnotations = cloneDeep(ruleValue.Annotations)
                        copyAnnotations[itemIndex].Name = value
                        onChangeValue({ Annotations: copyAnnotations }, index)
                      }}
                    />
                  )}
                </FormItem>
                <span>=</span>
                <FormItem
                  hideLabel
                  value={item.Value}
                  rules={{
                    required: true,
                    ruleDescription: true
                  }}
                >
                  <Input
                    size="small"
                    width={322}
                    value={item.Value}
                    type="textarea"
                    onChangeValue={value => {
                      const copyAnnotations = cloneDeep(ruleValue.Annotations)
                      copyAnnotations[itemIndex].Value = value
                      onChangeValue({ Annotations: copyAnnotations }, index)
                    }}
                  />
                </FormItem>
                {itemIndex > 1 ? (
                  <Icon
                    className="ion-trash-a"
                    size={16}
                    onClick={() => {
                      const copyAnnotations = cloneDeep(ruleValue.Annotations)
                      copyAnnotations.splice(itemIndex, 1)
                      onChangeValue(
                        {
                          Annotations: copyAnnotations
                        },
                        index
                      )
                    }}
                  />
                ) : null}
              </div>
            )
          })}
          <Button
            className="form_add_btn"
            size="small"
            onClick={() => {
              const copyAnnotations = cloneDeep(ruleValue.Annotations)
              copyAnnotations.push({ Name: '', Value: '' })
              onChangeValue(
                {
                  Annotations: copyAnnotations
                },
                index
              )
            }}
          >
            <Icon className="k-icon-add-bold" />
            添加
          </Button>
        </FormItem>
      </FormItem>
    </div>
  )
}

export default styled(RuleFormItem)`
  width: 700px;
  margin-bottom: 8px;
  .rule_item_header {
    width: 700px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 16px;
    transition: all 0.3s;
    background-color: #f0f2f4;
    cursor: pointer;
    &:hover {
      background-color: #dedede;
    }
    p {
      margin-bottom: 0;
    }
    .k-icon {
      line-height: 32px;
    }
  }
  .rule_item_container {
    width: 100%;
    padding: 16px 16px 32px 0;
    border: 1px solid #e2e5e8;
    background-color: #f0f2f4;
    &.rule_item_container_hide {
      height: 0px;
      padding: 0;
      border: none;
      overflow: hidden;
    }
    .header {
      width: 700px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0 16px;
    }
  }
  .k-form-label {
    width: 155px !important;
    .ion-help-circled {
      line-height: 26px !important;
    }
  }
  .form_item_expr > .k-form-content {
    width: 520px;
  }
  .form_item_list {
    & > .k-form-content {
      width: 520px;
      .list_item {
        display: flex;
        align-items: center;
        padding-bottom: 20px;
        /* height: 52px; */
        .list_item_key {
          margin-bottom: 0;
        }
        & > span {
          margin: 0 8px;
        }
        .ion-trash-a {
          margin-left: 8px;
        }
        .k-form-item {
          margin-bottom: 0;
        }
      }
    }
  }
  .for_form_item {
    .k-spinner {
      border-right: none;
      border-radius: 4px 0 0 4px;
    }
    .k-select {
      border-radius: 0 4px 4px 0;
    }
  }
  .annotations_form_item {
    display: flex;
    align-items: center;
    span {
      margin: 0 8px;
    }
  }
`
